import { useState, useEffect } from 'react';
import _isNil from 'lodash/isNil';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import _keyBy from 'lodash/keyBy';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { searchFulfillmentApi, productsGraphApi } from '../api/graphQL';
import { useDesignStore } from '../store/design/context';
import { pickPromoSkus, addFulfillment } from './utils';
import { getLocalizerZipCode } from '../utils/fulfillment-utils';
import logNewRelicCustom from '../utils/logNewRelicCustom';
import { NEW_RELIC_EVENTS } from '../constants';

export const useAsyncServiceGCC = (asyncFn, ...args) => {
  const [data, setData] = useState({ data: null, ready: false });
  const host = useConfigService('global-custom-commerce:host');

  useEffect(() => {
    const fetchData = async () => {
      const response = await asyncFn(host, ...args);
      const isReady = !_isNil(response);
      setData({
        data: response,
        ready: isReady
      });
    };
    if (!_isEmpty(...args)) {
      fetchData();
    }
  }, [...args]);

  return [data];
};

export const useAsyncCollectionServiceGCC = (asyncFn, ...args) => {
  const [data, setData] = useState({ data: null, ready: false });
  const host = useConfigService('global-custom-commerce:host');

  useEffect(() => {
    const fetchData = async () => {
      const response = await asyncFn(host, ...args);
      const isReady = !_isNil(response);
      if (_isNil(response)) {
        logNewRelicCustom(NEW_RELIC_EVENTS.ASYNC_COLLECTION_SERVICE_GCC, {
          asyncCollectionServiceGCC: {
            api: `${host}/${asyncFn.name}`,
            errorMessage: 'Not found',
            omsId: args
          }
        });
        window.location.replace('https://www.homedepot.com/c/kitchen_cabinet_collection_by_colors');
      }
      setData({
        data: response,
        ready: isReady
      });
    };
    if (!_isEmpty(...args)) {
      fetchData();
    }
  }, [...args]);

  return [data];
};

export const useAsyncServiceTHD = (itemIds) => {
  const [data, setData] = useState({ data: null, ready: false });
  const fulfillmentBaseUrl = useConfigService('graphQL:fulfillmentBaseUrl');
  const graphBaseUrl = useConfigService('graphQL:baseUrl');
  const productsPerBatch = useConfigService('graphQL:productsPerBatch');
  const fulfillmentPerBatch = useConfigService('graphQL:fulfillmentPerBatch');
  const store = useStore();
  const { storeId = '8119' } = store;
  const zipCode = getLocalizerZipCode();
  const summarySkus = useDesignStore((state) => _map(state.design.products, (item) => item?.Sku));
  const nonExpSummarySkus = useDesignStore((state) => _map(state.design.cart, (item) => item?.Sku));

  useEffect(() => {
    const fetchData = async (skus) => {
      if (!_isEmpty(skus)) {
        const response = await productsGraphApi(graphBaseUrl, productsPerBatch, skus, storeId, zipCode);
        return response;
      }
      return [];
    };

    const getFulfillment = async (skus) => {
      if (!_isEmpty(skus)) {
        const response = await searchFulfillmentApi(fulfillmentBaseUrl, fulfillmentPerBatch, skus, storeId, zipCode);
        return response;
      }
      return [];
    };

    const resolver = async (skus, checkForPromoSkus) => {
      const productData = await fetchData(skus);
      const fulfillmentData = await getFulfillment(skus);

      if (checkForPromoSkus) {
        const diff = pickPromoSkus(_keyBy(productData, 'itemId'), checkForPromoSkus);

        if (diff) {
          const promoProductData = await fetchData(diff) || [];
          const promoFulfillmentData = await getFulfillment(diff) || [];

          const productDataAndPromoDataWithFulfillment = addFulfillment(
            [...productData, ...promoProductData],
            _keyBy([...fulfillmentData, ...promoFulfillmentData], 'itemId')
          );

          return setData({
            data: _keyBy(productDataAndPromoDataWithFulfillment, 'itemId'),
            ready: true
          });
        }
      }

      const productDataWithFulfillment = addFulfillment(
        productData, _keyBy(fulfillmentData, 'itemId')
      );

      return setData({
        data: _keyBy(productDataWithFulfillment, 'itemId'),
        ready: true
      });
    };

    if (!_isEmpty(itemIds)) {
      const uniqProductsAndCart = _uniq([
        ...itemIds,
        ...summarySkus,
        ...nonExpSummarySkus
      ]);
      const checkForPromoSkus = [...summarySkus, ...nonExpSummarySkus];
      resolver(uniqProductsAndCart, checkForPromoSkus);
    }

  }, [itemIds]);

  return [data];
};
